import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { useEffect, useRef } from 'react';
import { useReportStore } from '../../stores/reportStore';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { sliceToPlainText } from '../../lib/reportClipboard';
import Toolbar from './Toolbar';
import { RefreshCw, Loader2, Tag, Brain, Cpu, Zap } from 'lucide-react';

const METHOD_LABELS: Record<string, { label: string; color: string; icon: typeof Brain }> = {
  llm: { label: 'LLM', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300', icon: Brain },
  sklearn: { label: 'ML Model', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', icon: Cpu },
  manual_override: { label: 'Manual', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300', icon: Zap },
  fallback: { label: 'Fallback', color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', icon: Tag },
};

export default function ReportEditor() {
  const {
    generatedReport,
    editedReport,
    isGenerating,
    pushToHistory,
    canUndo,
    canRedo,
    undo,
    redo,
    highlightedIssue,
    setHighlightedIssue,
    examCategory,
    templateName,
    classificationMethod,
    classificationConfidence,
  } = useReportStore();

  const { preferences } = usePreferencesStore();
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: editedReport || '',
    onUpdate: ({ editor }) => {
      // Sync latest content to store so clipboard/export always gets current state
      const newContent = editor.getHTML();
      useReportStore.getState().setEditedReport(newContent);
    },
    onBlur: ({ editor }) => {
      const newContent = editor.getHTML();
      if (newContent !== editedReport) {
        pushToHistory(newContent);
      }
    },
    editorProps: {
      attributes: {
        // Note: 'prose'/'prose-sm' are no-ops here because the project
        // doesn't have @tailwindcss/typography installed. Editor styling
        // lives in src/index.css under .ProseMirror selectors. Don't add
        // prose-* modifiers — they generate no CSS.
        class: 'max-w-none focus:outline-none',
        style: `font-size: ${preferences?.font_size || 14}px`,
      },
      // The radiologist's primary workflow is select-in-frame + Ctrl+C and
      // paste into Karisma. ProseMirror's default text serialization joins
      // blocks with bare \n — Karisma needs CRLF and a blank line between
      // paragraphs (tight after headings). This makes native copy produce
      // the same correctly-spaced plain text as the Copy button.
      clipboardTextSerializer: (slice) => sliceToPlainText(slice),
    },
  });

  // Update editor content when generated report changes
  useEffect(() => {
    if (editor && generatedReport && generatedReport !== editor.getHTML()) {
      editor.commands.setContent(generatedReport);
    }
  }, [editor, generatedReport]);

  // Update editor content when undo/redo happens
  useEffect(() => {
    if (editor && editedReport !== editor.getHTML()) {
      editor.commands.setContent(editedReport);
    }
  }, [editor, editedReport]);

  // Jump to an issue's location when it is clicked in the issues panel.
  // SELECTION ONLY — no highlight marks. setHighlight() writes a <mark>
  // into the document, and the radiologist's Karisma workflow is
  // select-all + copy out of this pane: a copy landing inside the old
  // 3-second highlight window carried the mark markup into Karisma.
  // A browser selection is visible, scrolls into view, and never touches
  // the document content.
  useEffect(() => {
    if (!editor) return;

    if (highlightedIssue?.location) {
      const searchText = highlightedIssue.location.toLowerCase();

      // Search through the document to find matching text
      let foundFrom = -1;
      let foundTo = -1;

      editor.state.doc.descendants((node, pos) => {
        if (foundFrom !== -1) return false; // Stop if already found

        if (node.isText && node.text) {
          const nodeTextLower = node.text.toLowerCase();
          const matchIndex = nodeTextLower.indexOf(searchText);

          if (matchIndex !== -1) {
            // Found the text in this node
            foundFrom = pos + matchIndex;
            foundTo = pos + matchIndex + searchText.length;
            return false; // Stop searching
          }
        }
        return true; // Continue searching
      });

      if (foundFrom !== -1 && foundTo !== -1) {
        editor.chain()
          .focus()
          .setTextSelection({ from: foundFrom, to: foundTo })
          .scrollIntoView()
          .run();
      }
    }

    // Clear the card's highlighted state after a few seconds (visual only —
    // the editor selection stays wherever the radiologist left it)
    const timer = setTimeout(() => {
      if (highlightedIssue) {
        setHighlightedIssue(null);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [editor, highlightedIssue]);

  const methodInfo = classificationMethod ? METHOD_LABELS[classificationMethod] || METHOD_LABELS.fallback : null;
  const MethodIcon = methodInfo?.icon || Tag;

  return (
    <div className="flex flex-col h-full">
      {/* Template info bar */}
      {(examCategory && generatedReport) || isGenerating ? (
        <div className="flex items-center justify-between mb-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-surface-900 border border-gray-200 dark:border-surface-800">
          <div className="flex items-center gap-1.5">
            {examCategory && generatedReport && (
              <>
                {templateName && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                    <Tag size={10} />
                    {templateName}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                  {examCategory.replace(/_/g, ' ')}
                </span>
                {methodInfo && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${methodInfo.color}`}>
                    <MethodIcon size={10} />
                    {methodInfo.label}
                    {classificationConfidence > 0 && (
                      <span className="opacity-70">
                        {Math.round(classificationConfidence * 100)}%
                      </span>
                    )}
                  </span>
                )}
              </>
            )}
          </div>
          {isGenerating && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 size={16} className="animate-spin" />
              Generating...
            </div>
          )}
        </div>
      ) : null}

      {/* Toolbar */}
      {editor && (
        <Toolbar
          editor={editor}
          canUndo={canUndo()}
          canRedo={canRedo()}
          onUndo={undo}
          onRedo={redo}
        />
      )}

      {/* Editor */}
      <div
        ref={editorContainerRef}
        className="flex-1 overflow-y-auto bg-white dark:bg-surface-950 rounded-lg border border-gray-200 dark:border-surface-800 p-4"
        style={{ fontSize: `${preferences?.font_size || 14}px` }}
      >
        {generatedReport || editedReport ? (
          <EditorContent editor={editor} className="min-h-full" />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
            <RefreshCw size={48} className="mb-4 opacity-50" />
            <p className="text-center">
              Record your dictation and click "Generate Report"
              <br />
              to create an AI-powered radiology report.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
